import { z } from "zod/v4-mini";

import { TandemCommand, TandemResponse } from "@/lib/drivers/tandem/TandemTypes";
import structFn from "@/lib/struct";
import { Schema, cz } from "@/utils/zodUtils";

const struct = structFn();

export type TimeDependentSegmentPayload = {
  startTime: number;
  basalRate: number;
  carbRatio: number;
  TargetBG: number;
  ISF: number;
  status: number;
};

export type PersonalProfilePayload = {
  carb_entry: number;
  idp: number;
  insulin_duration: number;
  max_bolus: number;
  name: string;
  status: number;
  tdep_num: number;
  tdeps: Uint8Array;
};

export type TimeDependentSegment = TimeDependentSegmentPayload;

export type PersonalProfile = {
  carbEntry: number;
  profileId: number;
  insulinDuration: number;
  maxBolus: number;
  name: string;
  status: "active" | "inactive";
  segmentsNum: number;
  segments: TimeDependentSegment[];
};

export const personalProfilePayloadSchema: Schema<PersonalProfilePayload> = z.object({
  carb_entry: z.number(),
  idp: z.number(),
  insulin_duration: z.number(),
  max_bolus: z.number(),
  name: z.string(),
  status: z.number(),
  tdep_num: z.number(),
  tdeps: cz.unsafe<Uint8Array>(),
});

export const timeDependentSegmentSchema: Schema<TimeDependentSegment> = z.object({
  startTime: z.number(),
  basalRate: z.number(),
  carbRatio: z.number(),
  TargetBG: z.number(),
  ISF: z.number(),
  status: z.number(),
});

export const TIME_DEPENDENT_SETTINGS_RESPONSE = {
  value: -1,
  name: "Time Dependent Settings Segment Structure",
  format: "hhihhb",
  fields: ["startTime", "basalRate", "carbRatio", "TargetBG", "ISF", "status"] as const,
  postprocess: (rec): TimeDependentSegment => {
    return {
      ...rec,
      startTime: rec.startTime * 60 * 1000, // convert minutes to milliseconds
      basalRate: rec.basalRate * 0.001,
      carbRatio: rec.carbRatio * 0.001,
    };
  },
  schema: timeDependentSegmentSchema,
} as const satisfies TandemResponse<TimeDependentSegmentPayload, TimeDependentSegment>;

export const PERSONAL_PROFILE_RESPONSE = {
  value: 176,
  name: "Personal Profile Message",
  version: 36102,
  format: "b17zb208Bhhbb",
  fields: ["idp", "name", "tdep_num", "tdeps", "insulin_duration", "max_bolus", "carb_entry", "status"] as const,
  postprocess: rec => {
    const segments: TimeDependentSegment[] = [];
    const segmentsLength = struct.structlen(TIME_DEPENDENT_SETTINGS_RESPONSE.format);
    for (let i = 0; i < rec.tdep_num; i++) {
      const data = struct.unpack(
        rec.tdeps,
        i * segmentsLength,
        TIME_DEPENDENT_SETTINGS_RESPONSE.format,
        TIME_DEPENDENT_SETTINGS_RESPONSE.fields,
      );
      const parsedData = TIME_DEPENDENT_SETTINGS_RESPONSE.schema.safeParse(data);
      if (parsedData.success) {
        const postProcess = TIME_DEPENDENT_SETTINGS_RESPONSE.postprocess;
        if (postProcess) {
          segments.push(postProcess(parsedData.data));
        } else {
          segments.push(parsedData.data);
        }
      }
    }
    return {
      carbEntry: rec.carb_entry,
      profileId: rec.idp,
      insulinDuration: rec.insulin_duration,
      maxBolus: rec.max_bolus * 0.001,
      name: rec.name,
      status: rec.status === 4 ? "active" : "inactive",
      segmentsNum: rec.tdep_num,
      segments,
    };
  },
  schema: personalProfilePayloadSchema,
} as const satisfies TandemResponse<PersonalProfilePayload, PersonalProfile>;

export const PERSONAL_PROFILE_COMMAND = {
  value: 175,
  name: "Personal Profile Request",
  version: 36102,
  format: "b",
  fields: ["idp"] as const,
  response: PERSONAL_PROFILE_RESPONSE,
} as const satisfies TandemCommand<PersonalProfilePayload, PersonalProfile>;
